import * as React from 'react'
import { AlertCircle, IdCard, X } from 'lucide-react'

import { CANADIAN_PROVINCES, US_STATES } from '@/shared/config/states'
import { DatePicker, FormInput, FormSelect } from '@/shared/ui/components'
import { Button } from '@/shared/ui/primitives'
import { cn } from '@/shared/ui/cn'
import type { IdentityDocumentContentType } from '@/modules/auth/use-cases'
import type { DriversLicenseEntry } from './form-values'
import { EMPTY_DRIVERS_LICENSE_ENTRY } from './form-values'

const MAX_FILE_SIZE_BYTES = 5 * 1024 * 1024

const ACCEPTED_CONTENT_TYPES: IdentityDocumentContentType[] = [
  'image/jpeg',
  'image/png',
  'image/heic',
  'image/heif',
]

const STATE_OPTIONS = [...US_STATES, ...CANADIAN_PROVINCES].map((s) => ({
  value: s.code,
  label: `${s.code} — ${s.name}`,
}))

export type DriversLicenseSectionProps = {
  entry?: DriversLicenseEntry
  errors: Record<string, string>
  isSubmitting: boolean
  onChange: (entry: DriversLicenseEntry) => void
}

function isAcceptedType(type: string): type is IdentityDocumentContentType {
  return (ACCEPTED_CONTENT_TYPES as string[]).includes(type)
}

function readAsBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onerror = () => reject(new Error('Could not read the selected file'))
    reader.onload = () => {
      const result = typeof reader.result === 'string' ? reader.result : ''
      const comma = result.indexOf(',')
      resolve(comma === -1 ? result : result.slice(comma + 1))
    }
    reader.readAsDataURL(file)
  })
}

export function DriversLicenseSection({
  entry,
  errors,
  isSubmitting,
  onChange,
}: DriversLicenseSectionProps) {
  const value = entry ?? EMPTY_DRIVERS_LICENSE_ENTRY
  const inputRef = React.useRef<HTMLInputElement>(null)
  const [fileError, setFileError] = React.useState<string | null>(null)

  const update = (patch: Partial<DriversLicenseEntry>) => {
    onChange({ ...value, ...patch })
  }

  const handleFile = async (file: File | undefined) => {
    if (!file) return

    if (!isAcceptedType(file.type)) {
      setFileError('Upload a JPEG, PNG or HEIC image')
      return
    }
    if (file.size > MAX_FILE_SIZE_BYTES) {
      setFileError('Image must be 5 MB or smaller')
      return
    }

    try {
      const fileBase64 = await readAsBase64(file)
      setFileError(null)
      update({ fileBase64, fileName: file.name, contentType: file.type })
    } catch {
      setFileError('Could not read the selected file')
    }
  }

  const clearFile = () => {
    setFileError(null)
    update({ fileBase64: '', fileName: '', contentType: 'image/jpeg' })
    if (inputRef.current) inputRef.current.value = ''
  }

  const photoError = fileError ?? errors.driversLicenseFile

  return (
    <div className="flex flex-col gap-4">
      <FormSelect
        id="driversLicenseIssuingState"
        label="Issuing state / province"
        required
        value={value.issuingState}
        onChange={(v) => update({ issuingState: v })}
        error={errors.driversLicenseIssuingState}
        disabled={isSubmitting}
        placeholder="Select state / province"
        options={STATE_OPTIONS}
      />

      <FormInput
        id="driversLicenseNumber"
        label="Licence number"
        required
        value={value.documentNumber}
        onChange={(v) => update({ documentNumber: v })}
        error={errors.driversLicenseNumber}
        disabled={isSubmitting}
      />

      <DatePicker
        id="driversLicenseExpiry"
        label="Expiry (optional)"
        value={value.expiry}
        onChange={(v) => update({ expiry: v ?? '' })}
        disabled={isSubmitting}
      />

      <div className="flex flex-col gap-2">
        <span className="text-sm font-medium">Photo of your licence</span>

        <input
          ref={inputRef}
          type="file"
          accept={ACCEPTED_CONTENT_TYPES.join(',')}
          capture="environment"
          className="sr-only"
          disabled={isSubmitting}
          onChange={(e) => void handleFile(e.target.files?.[0])}
        />

        {value.fileBase64 ? (
          <div className="flex items-center justify-between gap-3 rounded-md border p-3">
            <div className="flex min-w-0 items-center gap-2">
              <IdCard className="size-4 shrink-0" aria-hidden />
              <span className="truncate text-sm">{value.fileName}</span>
            </div>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              disabled={isSubmitting}
              onClick={clearFile}
            >
              <X className="size-4" aria-hidden />
              <span className="sr-only">Remove photo</span>
            </Button>
          </div>
        ) : (
          <Button
            type="button"
            variant="outline"
            disabled={isSubmitting}
            onClick={() => inputRef.current?.click()}
          >
            <IdCard className="size-4" aria-hidden />
            Add a photo
          </Button>
        )}

        <p
          className={cn(
            'text-xs',
            photoError ? 'text-destructive' : 'text-muted-foreground',
          )}
        >
          {photoError ? (
            <span className="flex items-center gap-1">
              <AlertCircle className="size-3" aria-hidden />
              {photoError}
            </span>
          ) : (
            'JPEG, PNG or HEIC, up to 5 MB. Only your organization managers can view it.'
          )}
        </p>
      </div>
    </div>
  )
}
